import React from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@mui/material';
import { Box } from '@mui/system';

import {
  authorColor, authorInitials, getAuthorImageUrl, getAuthorName, mergeAuthor,
} from '../../common/ItemTools';
import {
  MUTED, TEXT_DARK, WARNING_BG, WARNING_BORDER, WARNING_HINT,
} from '../common/tokens';

const AVATAR_SIZE = 26;

const nameSx = {
  fontWeight: 600,
  fontSize: '.95rem',
  color: TEXT_DARK,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Amber rather than the panel's coral: a heads-up to read before clicking, not a
// second accent competing with the source badge.
const newTagSx = {
  flexShrink: 0,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  padding: '2px 5px',
  borderRadius: '4px',
  lineHeight: 1.4,
  background: WARNING_BG,
  border: `1px solid ${WARNING_BORDER}`,
  color: WARNING_HINT,
};

function Face({ imageUrl, name }) {
  if (imageUrl) {
    return (
      <Avatar
        src={imageUrl}
        alt={name}
        sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, flexShrink: 0 }}
      />
    );
  }
  return (
    <Box sx={{
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: '50%',
      background: authorColor(name),
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      fontWeight: 700,
      flexShrink: 0,
    }}>
      {authorInitials(name)}
    </Box>
  );
}

Face.propTypes = {
  imageUrl: PropTypes.string,
  name: PropTypes.string,
};

Face.defaultProps = { imageUrl: null, name: '' };

/**
 * An author suggestion's value. Picking one either links an author the user already
 * has or creates a new record. An existing author shows their face and stored name,
 * a new one is tagged.
 */
export default function AuthorCandidateValue({ candidate }) {
  const observed = candidate.name || candidate.username || '';
  const existing = candidate.author ? mergeAuthor(candidate.author) : null;

  if (!existing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
        <Box sx={nameSx}>{observed}</Box>
        <Box component="span" sx={newTagSx}>new author</Box>
      </Box>
    );
  }

  const name = getAuthorName(existing) || observed;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
      <Face imageUrl={getAuthorImageUrl(existing, existing.authorImageScope)} name={name} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={nameSx}>{name}</Box>
        {/* The byline can be phrased differently from the stored name — show it when it is. */}
        {observed && observed.toLowerCase() !== name.toLowerCase() && (
          <Box sx={{ fontSize: 11, color: MUTED }}>{`found as “${observed}”`}</Box>
        )}
      </Box>
    </Box>
  );
}

AuthorCandidateValue.propTypes = { candidate: PropTypes.object.isRequired };
